import { useState, useRef, useEffect, useCallback } from "react";
import { BaseAppProps } from "../../../types/webos";

import dogImage from "url:../AnimalSounds/images/dog.svg";
import cowImage from "url:../AnimalSounds/images/cow.svg";
import duckImage from "url:../AnimalSounds/images/duck.svg";
import pigImage from "url:../AnimalSounds/images/pig.svg";
import horseImage from "url:../AnimalSounds/images/horse.svg";
import sheepImage from "url:../AnimalSounds/images/sheep.svg";
import chickenImage from "url:../AnimalSounds/images/chicken.svg";
import goatImage from "url:../AnimalSounds/images/goat.svg";
import donkeyImage from "url:../AnimalSounds/images/donkey.svg";

import dogSoundUrl from "url:../AnimalSounds/sounds/dog.mp3";
import bullSoundUrl from "url:../AnimalSounds/sounds/bull.mp3";
import duckSoundUrl from "url:../AnimalSounds/sounds/duck.mp3";
import pigSoundUrl from "url:../AnimalSounds/sounds/pig.mp3";
import horseSoundUrl from "url:../AnimalSounds/sounds/horse.mp3";
import sheepSoundUrl from "url:../AnimalSounds/sounds/sheep.mp3";
import roosterSoundUrl from "url:../AnimalSounds/sounds/rooster.mp3";
import goatSoundUrl from "url:../AnimalSounds/sounds/goat.mp3";
import donkeySoundUrl from "url:../AnimalSounds/sounds/donkey.mp3";

interface AnimalDef {
  name: string;
  image: string;
  sound: string;
}

const ANIMALS: AnimalDef[] = [
  { name: "Chien",  image: dogImage,     sound: dogSoundUrl },
  { name: "Vache",  image: cowImage,     sound: bullSoundUrl },
  { name: "Canard", image: duckImage,    sound: duckSoundUrl },
  { name: "Cochon", image: pigImage,     sound: pigSoundUrl },
  { name: "Cheval", image: horseImage,   sound: horseSoundUrl },
  { name: "Mouton", image: sheepImage,   sound: sheepSoundUrl },
  { name: "Poule",  image: chickenImage, sound: roosterSoundUrl },
  { name: "Chèvre", image: goatImage,    sound: goatSoundUrl },
  { name: "Âne",    image: donkeyImage,  sound: donkeySoundUrl },
  { name: "Chien",  image: dogImage,     sound: dogSoundUrl },
  { name: "Vache",  image: cowImage,     sound: bullSoundUrl },
  { name: "Canard", image: duckImage,    sound: duckSoundUrl },
  { name: "Cochon", image: pigImage,     sound: pigSoundUrl },
  { name: "Cheval", image: horseImage,   sound: horseSoundUrl },
  { name: "Mouton", image: sheepImage,   sound: sheepSoundUrl },
  { name: "Poule",  image: chickenImage, sound: roosterSoundUrl },
];

const MIDI_NOTE_TO_INDEX: Record<number, number> = {
  48: 0,  50: 1,  52: 2,  55: 3,
  57: 4,  60: 5,  62: 6,  64: 7,
  67: 8,  69: 9,  72: 10, 74: 11,
  76: 12, 79: 13, 81: 14, 84: 15,
};

export function App({}: BaseAppProps) {
  const [activeIndex, setActiveIndex]     = useState<number | null>(null);
  const [midiConnected, setMidiConnected] = useState(false);
  const audioRef       = useRef<HTMLAudioElement | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const playAnimal = useCallback((index: number) => {
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    setActiveIndex(index);
    highlightTimer.current = setTimeout(() => setActiveIndex(null), 600);

    if (navigator.vibrate) navigator.vibrate(100);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    const audio = new Audio(ANIMALS[index].sound);
    audioRef.current = audio;
    audio.play().catch(() => {});
  }, []);

  useEffect(() => {
    if (!navigator.requestMIDIAccess) return;
    let access: any;

    const onMessage = (event: any) => {
      const [cmd, note, vel] = event.data;
      if ((cmd & 0xf0) === 0x90 && vel > 0) {
        const idx = MIDI_NOTE_TO_INDEX[note as number];
        if (idx !== undefined) playAnimal(idx);
      }
    };

    const setup = () => {
      for (const input of access.inputs.values()) {
        input.onmidimessage = onMessage;
      }
      setMidiConnected(access.inputs.size > 0);
    };

    (navigator as any).requestMIDIAccess().then((a: any) => {
      access = a;
      setup();
      access.onstatechange = setup;
    }).catch(() => {});

    return () => {
      if (access) {
        for (const input of access.inputs.values()) {
          input.onmidimessage = null;
        }
      }
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      if (audioRef.current) audioRef.current.pause();
    };
  }, [playAnimal]);

  return (
    <div className="relative h-full bg-gradient-to-br from-green-200 via-yellow-100 to-orange-200 p-2 overflow-hidden">
      <div className="grid grid-cols-4 grid-rows-4 gap-2 h-full">
        {ANIMALS.map((animal, i) => (
          <button
            key={i}
            onClick={() => playAnimal(i)}
            className={`flex flex-col items-center justify-center rounded-2xl transition-all duration-150 select-none touch-manipulation
              ${activeIndex === i
                ? "bg-yellow-300 scale-95 shadow-inner ring-4 ring-yellow-500"
                : "bg-white/70 shadow-md active:scale-95"}`}
          >
            <img
              src={animal.image}
              alt={animal.name}
              className="w-1/2 h-1/2 object-contain pointer-events-none"
              draggable={false}
            />
            <span className="text-xs font-bold text-gray-800 mt-1 truncate w-full text-center px-1">
              {animal.name}
            </span>
          </button>
        ))}
      </div>

      <div className="absolute bottom-2 right-2 flex items-center gap-1.5 bg-black/40 text-white text-xs px-2 py-1 rounded-full pointer-events-none">
        <span className={`w-2 h-2 rounded-full ${midiConnected ? "bg-green-400" : "bg-gray-500"}`} />
        {midiConnected ? "MIDI connecté" : "Pas de MIDI"}
      </div>

    </div>
  );
}
